import React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import errorResponses from '../../../errorResponses';

import { AppContext } from '../../../App';
import { IExtractionBatch } from '../../../types/my-types';
import { useQueryClient } from '@tanstack/react-query';
import { useExtractionBatchClean } from '../../hooks/useExtractionBatchClean';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import SanitizerOutlinedIcon from '@mui/icons-material/SanitizerOutlined';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';

interface IProps {
  extractionBatch: IExtractionBatch;
  type: 'menu' | 'icon';
  handleClose?: () => void;
  afterActionSuccess?: () => void;
}

function ExtractionBatchClean({ extractionBatch, type, handleClose, afterActionSuccess }: IProps) {
  const { setToast } = React.useContext(AppContext);

  const [open, setOpen] = React.useState(false);

  const runHandleClose = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseClick = () => {
    setOpen(false);
    runHandleClose();
  };

  const cache = useQueryClient();

  const cleanMutation = useExtractionBatchClean({
    onSuccess: () => {
      cache.invalidateQueries({
        queryKey: ['extractionBatches']
      });
      cache.invalidateQueries({
        queryKey: ['extractionBatch']
      });
      cache.invalidateQueries({
        queryKey: ['allDocuments']
      });
      cache.invalidateQueries({
        queryKey: ['allExtractions']
      });
      cache.invalidateQueries({
        queryKey: ['extraction']
      });
      cache.invalidateQueries({
        queryKey: ['extraction-validation']
      });

      setToast({
        variant: 'success',
        open: true,
        message: 'Force Clean/Normalize Submitted.',
      });

      if (afterActionSuccess) {
        afterActionSuccess();
      }
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToCleanAndNormalize,
      });
    },
  });

  const handleSubmit = async () => {
    await cleanMutation.mutate(extractionBatch.id);
    setOpen(false);
    runHandleClose();
  };

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          id="btn-clean-eb"
          data-cy={`btn-clean-eb`}
          key={'Clean & Normalize'}
          onClick={handleClickOpen}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
          disabled={!extractionBatch.finished}
        >
          <ListItemIcon>
            <SanitizerOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {cleanMutation.isPending ? <CircularProgress size={20} /> : null}{' '}
          <MenuItemHiddenTextLabel>Clean & Normalize</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <IconButtonWhiteBg
          disabled={!extractionBatch.finished}
          onClick={handleClickOpen}
          id="icon-re-transform"
          cypressId={`icon-clean-and-normalize`}
          tooltip="Clean & Normalize"
        >
          <SanitizerOutlinedIcon style={{ fontSize: '15px', color: 'black' }} />
        </IconButtonWhiteBg>
      ) : null}

      <DialogGlynt open={open} closeModal={handleCloseClick} cypressId="dialog-clean-normalize">
        <DialogTitleGlynt title="Confirmation" closeModal={handleCloseClick} />
        <DialogContentGlynt>
          <Typography variant="body2" color="textSecondary">
            Under most circumstances, you should not need to force cleaning and normalizing. Are you sure you want to continue?
          </Typography>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={handleCloseClick}
          cancelCypressId="btn-clean-cancel"
          submitCypressId="btn-clean-yes"
          clearCypressId=""
          submitButtonText="Yes"
        />
      </DialogGlynt>
    </>
  );
}

export default ExtractionBatchClean;
